/* ==========================================================================
   vendingService

   REAL MODE: the purchase goes through the "gomelong" Supabase Edge Function
   (supabase/functions/gomelong), which is the only thing that talks to the
   Gomelong STS vending API. The token you see comes back from Gomelong. This
   file never holds Gomelong credentials and never retries a purchase, because
   the provider's token call has no idempotency key and a retry could vend twice.

   A purchase ends in one of three ways: success (token), failed (provider
   clearly refused; wallet refunded), or pending (answer uncertain; wallet stays
   charged and the purchase is held for a person to review).

   MOCK MODE (demo data only): simulated locally with a placeholder token from
   ./demoToken.ts. Nothing here is real.
   ========================================================================== */

import { config } from "../config";
import { getSupabase } from "./supabaseClient";
import { mockDb } from "./mock/mockDb";
import { delay, genReference, shouldSimulateFailure } from "./mock/mockUtils";
import { generateDemoTokenDigits } from "./demoToken";
import { requireActiveEstateId } from "../state/estateContext";
import type { BuyElectricityRequest, VendResult } from "../types";

// Demo-only estimated tariff, used solely to show a plausible kWh figure
// against the demo token. A real vending API returns the actual units
// purchased — this constant must not survive into a live integration.
const DEMO_KWH_PER_NAIRA = 0.00696;

const UNSURE_MESSAGE =
  "We couldn't confirm whether this purchase went through. Please don't try again yet. Check your transaction history in a minute or two.";

async function buyElectricity(req: BuyElectricityRequest): Promise<VendResult> {
  const estateId = requireActiveEstateId();

  if (config.useMockServices) {
    const meter = mockDb.getMeters(estateId).find((m) => m.id === req.meterId);
    if (!meter) {
      return { status: "failed", failureReason: "Selected meter could not be found." };
    }

    const currentBalance = mockDb.getWalletBalanceKobo(estateId);
    if (req.amountKobo > currentBalance) {
      return { status: "failed", failureReason: "Insufficient wallet balance." };
    }

    await delay(1000);

    if (shouldSimulateFailure()) {
      return { status: "failed", failureReason: "Vending provider could not complete this purchase (simulated)." };
    }

    mockDb.debitWallet(estateId, req.amountKobo);

    const reference = genReference("VEND");
    const nairaAmount = req.amountKobo / 100;
    const units = `${(nairaAmount * DEMO_KWH_PER_NAIRA).toFixed(1)} kWh`;
    const tokenDigits = generateDemoTokenDigits(`${req.amountKobo}-${meter.serial}-${Date.now()}`);

    mockDb.addTransaction(estateId, {
      type: "electricity_purchase",
      status: "success",
      amountKobo: req.amountKobo,
      direction: "debit",
      description: "Electricity Purchase",
      reference,
      meterId: meter.id,
      meterSerial: meter.serial,
      units,
    });

    return {
      status: "success",
      token: {
        token: tokenDigits,
        meterId: meter.id,
        meterSerial: meter.serial,
        amountKobo: req.amountKobo,
        units,
        transactionReference: reference,
        issuedAt: new Date().toISOString(),
        status: "success",
      },
    };
  }

  const supabase = getSupabase();
  const { data: meterRow, error: meterErr } = await supabase
    .from("meters")
    .select("serial")
    .eq("id", req.meterId)
    .single();
  if (meterErr || !meterRow) {
    return { status: "failed", failureReason: "Selected meter could not be found." };
  }

  // The "gomelong" Edge Function holds the money, asks the vending provider for
  // the token exactly once, then either saves the token, refunds, or holds the
  // purchase for review. Nothing here retries: a retry could vend twice.
  let data: any;
  let error: unknown;
  try {
    ({ data, error } = await supabase.functions.invoke("gomelong", {
      body: { action: "vend", meterId: req.meterId, estateId, amountKobo: req.amountKobo },
    }));
  } catch {
    // We can't tell whether the request reached the server, so never say "failed":
    // that would invite a retry while money may already be held.
    return { status: "pending", message: UNSURE_MESSAGE };
  }

  if (error) {
    const status = (error as { context?: { status?: number } }).context?.status;
    if (typeof status === "number" && status >= 400 && status < 500) {
      return { status: "failed", failureReason: "We couldn't start this purchase. Please sign in again and retry. You have not been charged." };
    }
    return { status: "pending", message: UNSURE_MESSAGE };
  }

  if (data?.status === "success" && data.token) {
    return {
      status: "success",
      token: {
        token: data.token.token,
        meterId: req.meterId,
        meterSerial: meterRow.serial,
        amountKobo: req.amountKobo,
        units: data.token.units ?? "",
        transactionReference: data.token.reference,
        issuedAt: data.token.issuedAt,
        status: "success",
        testMode: !!data.token.testMode,
        practice: !!data.token.practice,
      },
    };
  }
  if (data?.status === "pending") {
    return { status: "pending", reference: data.reference, message: data.message ?? UNSURE_MESSAGE };
  }
  return { status: "failed", failureReason: data?.failureReason ?? "This purchase could not be completed." };
}

export const vendingService = { buyElectricity };
